import { FileText, Mail, Paperclip, Send, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const AddRemarkModal = ({ open, onOpenChange, remarkForm, setRemarkForm, handleAddRemark, submitting }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="bg-[#1A1A1A] border-[#262626]" aria-describedby="add-remark-description">
      <DialogHeader>
        <DialogTitle className="text-white">Add Remark</DialogTitle>
        <p id="add-remark-description" className="text-sm text-gray-500">Add a remark or change request</p>
      </DialogHeader>
      <form onSubmit={handleAddRemark} className="space-y-4">
        <div><Label className="text-gray-400">Type</Label>
          <Select value={remarkForm.remark_type} onValueChange={(value) => setRemarkForm({...remarkForm, remark_type: value})}>
            <SelectTrigger className="bg-[#171717] border-[#262626]"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#1A1A1A] border-[#262626]">
              <SelectItem value="general">General</SelectItem>
              <SelectItem value="change_request">Change Request</SelectItem>
              <SelectItem value="approval">Approval</SelectItem>
              <SelectItem value="issue">Issue</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div><Label className="text-gray-400">Content *</Label><Textarea value={remarkForm.content} onChange={(e) => setRemarkForm({...remarkForm, content: e.target.value})} placeholder="Enter your remark..." className="bg-[#171717] border-[#262626] min-h-[100px]" required /></div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="border-[#262626]">Cancel</Button>
          <Button type="submit" disabled={submitting} className="bg-[#FF4500] hover:bg-[#E65100]">{submitting ? "Adding..." : "Add Remark"}</Button>
        </DialogFooter>
      </form>
    </DialogContent>
  </Dialog>
);

export const SendEmailModal = ({ open, onOpenChange, project, emailForm, setEmailForm, handleSendEmail, submitting }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="bg-[#1A1A1A] border-[#262626]" aria-describedby="send-email-description">
      <DialogHeader>
        <DialogTitle className="text-white">Send Email to Client</DialogTitle>
        <p id="send-email-description" className="text-sm text-gray-500">This is optional - only sent when you confirm</p>
      </DialogHeader>
      <div className="space-y-4">
        <div><Label className="text-gray-400">To</Label><p className="text-white">{project.client?.name} ({project.client?.email})</p></div>
        <div><Label className="text-gray-400">Subject</Label><Input value={emailForm.subject} onChange={(e) => setEmailForm({...emailForm, subject: e.target.value})} className="bg-[#171717] border-[#262626]" /></div>
        <div><Label className="text-gray-400">Custom Message (Optional)</Label><Textarea value={emailForm.custom_message} onChange={(e) => setEmailForm({...emailForm, custom_message: e.target.value})} className="bg-[#171717] border-[#262626]" /></div>
      </div>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="border-[#262626]">Cancel</Button>
        <Button onClick={handleSendEmail} disabled={submitting} className="bg-[#FF4500] hover:bg-[#E65100]"><Send className="w-4 h-4 mr-2" />{submitting ? "Sending..." : "Send"}</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export const EditProjectModal = ({ open, onOpenChange, editForm, setEditForm, handleUpdateProject, submitting }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="bg-[#1A1A1A] border-[#262626]" aria-describedby="edit-project-description">
      <DialogHeader>
        <DialogTitle className="text-white">Edit Project</DialogTitle>
        <p id="edit-project-description" className="text-sm text-gray-500">Update project financial details</p>
      </DialogHeader>
      <form onSubmit={handleUpdateProject} className="space-y-4">
        <div><Label className="text-gray-400">Final Sqft (After Stage 1 confirmation)</Label><Input type="number" value={editForm.final_sqft} onChange={(e) => setEditForm({...editForm, final_sqft: e.target.value})} placeholder="Confirmed sqft" className="bg-[#171717] border-[#262626]" /></div>
        <div><Label className="text-gray-400">Final Agreed Amount (₹)</Label><Input type="number" value={editForm.final_agreed_amount} onChange={(e) => setEditForm({...editForm, final_agreed_amount: e.target.value})} className="bg-[#171717] border-[#262626]" /></div>
        <div><Label className="text-gray-400">Discount Amount (₹)</Label><Input type="number" value={editForm.discount_amount} onChange={(e) => setEditForm({...editForm, discount_amount: e.target.value})} className="bg-[#171717] border-[#262626]" /></div>
        <div><Label className="text-gray-400">GST Percentage</Label><Input type="number" value={editForm.gst_percentage} onChange={(e) => setEditForm({...editForm, gst_percentage: e.target.value})} className="bg-[#171717] border-[#262626]" /></div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="border-[#262626]">Cancel</Button>
          <Button type="submit" disabled={submitting} className="bg-[#FF4500] hover:bg-[#E65100]">{submitting ? "Saving..." : "Save"}</Button>
        </DialogFooter>
      </form>
    </DialogContent>
  </Dialog>
);

export const UploadDocumentModal = ({ open, onOpenChange, project, uploadFile, setUploadFile, uploadDocType, setUploadDocType, sendToClient, setSendToClient, handleConfirmUpload, uploading }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="bg-[#1A1A1A] border-[#262626]" aria-describedby="upload-doc-description">
      <DialogHeader>
        <DialogTitle className="text-white flex items-center gap-2"><Upload className="w-5 h-5 text-[#FF4500]" /> Upload Document</DialogTitle>
        <p id="upload-doc-description" className="text-sm text-gray-500">Upload scope of work, contract, or other documents</p>
      </DialogHeader>
      <div className="space-y-4">
        {uploadFile && (
          <div className="p-4 bg-[#171717] rounded border border-[#262626]">
            <div className="flex items-center gap-3">
              <FileText className="w-8 h-8 text-[#FF4500]" />
              <div>
                <p className="text-white font-medium">{uploadFile.name}</p>
                <p className="text-xs text-gray-500">{(uploadFile.size / 1024).toFixed(1)} KB</p>
              </div>
            </div>
          </div>
        )}

        <div>
          <Label className="text-gray-400">Document Type</Label>
          <Select value={uploadDocType} onValueChange={setUploadDocType}>
            <SelectTrigger className="bg-[#171717] border-[#262626]"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#1A1A1A] border-[#262626]">
              <SelectItem value="scope_of_work">📋 Scope of Work</SelectItem>
              <SelectItem value="contract">📄 Contract</SelectItem>
              <SelectItem value="proposal">📝 Proposal</SelectItem>
              <SelectItem value="minutes">📌 Meeting Minutes</SelectItem>
              <SelectItem value="drawing">🏗️ Drawing</SelectItem>
              <SelectItem value="other">📁 Other</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {uploadDocType === "scope_of_work" && (
          <div className="space-y-3 p-4 bg-[#171717] rounded border border-[#262626]">
            <div className="flex items-center gap-2 mb-2">
              <Mail className="w-4 h-4 text-[#FF4500]" />
              <p className="text-sm font-medium text-white">Email to Client</p>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-white">Send Scope of Work</p>
                <p className="text-xs text-gray-500">PDF will be attached to email → {project?.client?.email || "client email"}</p>
              </div>
              <Switch
                checked={sendToClient}
                onCheckedChange={setSendToClient}
                disabled={!project?.client?.email}
              />
            </div>
          </div>
        )}
      </div>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={() => { onOpenChange(false); setUploadFile(null); }} className="border-[#262626]">Cancel</Button>
        <Button onClick={handleConfirmUpload} disabled={uploading || !uploadFile} className="bg-[#FF4500] hover:bg-[#E65100]">
          {sendToClient ? <><Paperclip className="w-4 h-4 mr-2" />{uploading ? "Sending..." : "Upload & Email PDF"}</> : <><Upload className="w-4 h-4 mr-2" />{uploading ? "Uploading..." : "Upload"}</>}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);
